/**
 * Totem State Machine
 *
 * Estados: waiting, stabilizing, recognizing, identified, retrying
 *
 * Transições portadas da POC:
 * - waiting -> stabilizing quando face estável detectada.
 * - stabilizing -> recognizing ao atingir estabilidade (2s).
 * - recognizing -> identified (success) ou retrying (fail).
 * - identified permanece até nova face ou ausência confirmada.
 * - retrying -> waiting após cooldown.
 */

export type TotemState = "waiting" | "stabilizing" | "recognizing" | "identified" | "retrying";

export type StateEvent =
  | { type: "face_stable" }
  | { type: "face_unstable" }
  | { type: "face_absent" }
  | { type: "face_multiple" }
  | { type: "recognize_success"; recorded: boolean }
  | { type: "recognize_fail" }
  | { type: "retry_ready" };

export interface TotemStateMachineOptions {
  retryCooldownMs?: number;
}

export class TotemStateMachine {
  state: TotemState = "waiting";
  private retryCooldownMs: number;

  constructor(options: TotemStateMachineOptions = {}) {
    this.retryCooldownMs = options.retryCooldownMs ?? 3000;
  }

  transition(event: StateEvent): TotemState {
    switch (this.state) {
      case "waiting": {
        if (event.type === "face_stable") {
          this.state = "stabilizing";
        }
        break;
      }
      case "stabilizing": {
        if (event.type === "face_stable") {
          this.state = "recognizing";
        } else if (event.type === "face_unstable" || event.type === "face_absent" || event.type === "face_multiple") {
          this.state = "waiting";
        }
        break;
      }
      case "recognizing": {
        if (event.type === "recognize_success") {
          this.state = "identified";
        } else if (event.type === "recognize_fail") {
          this.state = "retrying";
        } else if (event.type === "face_absent") {
          this.state = "waiting";
        }
        // face_stable/unstable/multiple durante recognizing: ignora, mantém recognizing
        break;
      }
      case "identified": {
        if (event.type === "face_absent") {
          this.state = "waiting";
        } else if (event.type === "face_stable") {
          // Nova face detectada após identified: rearma
          this.state = "stabilizing";
        } else if (event.type === "face_multiple") {
          this.state = "waiting";
        }
        break;
      }
      case "retrying": {
        if (event.type === "retry_ready") {
          this.state = "waiting";
        } else if (event.type === "face_absent") {
          this.state = "waiting";
        } else if (event.type === "face_stable") {
          this.state = "waiting";
        } else if (event.type === "recognize_success") {
          this.state = "identified";
        }
        break;
      }
    }
    return this.state;
  }

  reset(): void {
    this.state = "waiting";
  }
}
